import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ModalCadastro } from './Modais';
import { BotaoPadrao } from './Botoes';
import '../assets/css/document-scanner.css';

/**
 * Scanner de documento (porte do document-scanner do hub).
 *
 * Fluxo: carrega o OpenCV.js → detecta as bordas do papel (pipeline "simple-scan")
 * → corrige a perspectiva → mostra o resultado. Se a detecção falhar, ou se o
 * usuário clicar em "Editar", os 4 cantos ficam arrastáveis sobre a imagem.
 *
 * O OpenCV é servido da própria aplicação (public/opencv/opencv.js) e só é
 * baixado na primeira vez que o editor abre.
 */

const OPENCV_SRC = (process.env.PUBLIC_URL || '') + '/opencv/opencv.js';
const OPENCV_SCRIPT_ID = 'opencv-js-loader';
const LARGURA_SAIDA = 1240;
const DIMENSAO_DETECCAO = 1080;
const CHAVES_CANTOS = ['tl', 'tr', 'br', 'bl'];

// ---------------------------------------------------------------------------
// OpenCV
// ---------------------------------------------------------------------------

function obterCv() {
    return window.cv && window.cv.Mat ? window.cv : null;
}

function carregarOpenCV() {
    if (obterCv()) return Promise.resolve();

    const scriptCarregado = document.getElementById(OPENCV_SCRIPT_ID)
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = OPENCV_SCRIPT_ID;
            script.src = OPENCV_SRC;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Falha ao baixar o OpenCV.js'));
            document.body.appendChild(script);
        });

    // O opencv.js define window.cv antes de o runtime (WASM) ficar pronto:
    // espera até cv.Mat existir, com teto de 30s.
    return scriptCarregado.then(() => new Promise((resolve) => {
        const inicio = Date.now();
        const verificar = () => {
            const cv = window.cv;
            if (cv && cv.Mat) {
                resolve();
                return;
            }
            if (cv && typeof cv.then === 'function') {
                cv.then((resolvido) => {
                    window.cv = resolvido;
                    resolve();
                });
                return;
            }
            if (cv && 'onRuntimeInitialized' in cv) {
                cv.onRuntimeInitialized = () => resolve();
                return;
            }
            if (Date.now() - inicio > 30000) {
                resolve();
                return;
            }
            setTimeout(verificar, 50);
        };
        verificar();
    }));
}

// ---------------------------------------------------------------------------
// Canvas
// ---------------------------------------------------------------------------

async function arquivoParaCanvas(arquivo) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas indisponível');

    // createImageBitmap respeita a orientação EXIF (foto de celular).
    if (typeof createImageBitmap === 'function') {
        try {
            const bitmap = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            ctx.drawImage(bitmap, 0, 0);
            bitmap.close();
            return canvas;
        } catch (e) {
            // cai para o carregamento por <img>
        }
    }

    const url = URL.createObjectURL(arquivo);
    try {
        const imagem = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = () => reject(new Error('Não foi possível ler a imagem'));
            i.src = url;
        });
        canvas.width = imagem.naturalWidth;
        canvas.height = imagem.naturalHeight;
        ctx.drawImage(imagem, 0, 0);
        return canvas;
    } finally {
        URL.revokeObjectURL(url);
    }
}

// Gira em múltiplos de 90°. Devolve o próprio canvas quando 0°.
function girarCanvas(origem, graus) {
    const normalizado = ((graus % 360) + 360) % 360;
    if (normalizado === 0) return origem;

    const saida = document.createElement('canvas');
    const ctx = saida.getContext('2d');
    if (!ctx) return origem;

    if (normalizado === 90 || normalizado === 270) {
        saida.width = origem.height;
        saida.height = origem.width;
    } else {
        saida.width = origem.width;
        saida.height = origem.height;
    }
    ctx.translate(saida.width / 2, saida.height / 2);
    ctx.rotate((normalizado * Math.PI) / 180);
    ctx.drawImage(origem, -origem.width / 2, -origem.height / 2);
    return saida;
}

function reduzirCanvas(origem, dimensaoMaxima) {
    const maiorLado = Math.max(origem.width, origem.height);
    if (maiorLado <= dimensaoMaxima) return { canvas: origem, escala: 1 };

    const escala = dimensaoMaxima / maiorLado;
    const saida = document.createElement('canvas');
    saida.width = Math.round(origem.width * escala);
    saida.height = Math.round(origem.height * escala);
    saida.getContext('2d').drawImage(origem, 0, 0, saida.width, saida.height);
    return { canvas: saida, escala };
}

// ---------------------------------------------------------------------------
// Detecção das bordas — pipeline "simple-scan" (learnopencv) em OpenCV.js:
// resize → morphologyEx CLOSE (5×5 × 5) → gray → GaussianBlur 11×11 →
// Canny(0, 200) → dilate 21×21 elíptico → contornos top-5 → approxPolyDP.
// ---------------------------------------------------------------------------

function ordenarCantos(pontos) {
    let tl = pontos[0], tr = pontos[0], br = pontos[0], bl = pontos[0];
    let menorSoma = Infinity, maiorSoma = -Infinity, menorDif = Infinity, maiorDif = -Infinity;

    pontos.forEach((p) => {
        const soma = p.x + p.y;
        const dif = p.x - p.y;
        if (soma < menorSoma) { menorSoma = soma; tl = p; }
        if (soma > maiorSoma) { maiorSoma = soma; br = p; }
        if (dif > maiorDif) { maiorDif = dif; tr = p; }
        if (dif < menorDif) { menorDif = dif; bl = p; }
    });

    return { tl, tr, br, bl };
}

function detectarCantos(canvas) {
    const cv = obterCv();
    if (!cv) return null;

    const { canvas: trabalho, escala } = reduzirCanvas(canvas, DIMENSAO_DETECCAO);
    const inverso = 1 / escala;

    const src = cv.imread(trabalho);
    const fechado = new cv.Mat();
    const cinza = new cv.Mat();
    const borrado = new cv.Mat();
    const bordas = new cv.Mat();
    const dilatado = new cv.Mat();
    const contornos = new cv.MatVector();
    const hierarquia = new cv.Mat();
    const kernelMorf = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5));
    const kernelDilat = cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(21, 21));

    try {
        cv.morphologyEx(src, fechado, cv.MORPH_CLOSE, kernelMorf, new cv.Point(-1, -1), 5);
        cv.cvtColor(fechado, cinza, cv.COLOR_RGBA2GRAY);
        cv.GaussianBlur(cinza, borrado, new cv.Size(11, 11), 0);
        cv.Canny(borrado, bordas, 0, 200);
        cv.dilate(bordas, dilatado, kernelDilat, new cv.Point(-1, -1), 1);
        cv.findContours(dilatado, contornos, hierarquia, cv.RETR_LIST, cv.CHAIN_APPROX_NONE);

        const entradas = [];
        for (let i = 0; i < contornos.size(); i++) {
            const contorno = contornos.get(i);
            entradas.push({ contorno, area: cv.contourArea(contorno) });
        }
        entradas.sort((a, b) => b.area - a.area);

        const areaImagem = trabalho.width * trabalho.height;
        const epsilons = [0.02, 0.03, 0.04, 0.015, 0.05];
        let melhor = null;
        let melhorArea = 0;

        entradas.slice(0, 5).forEach(({ contorno, area }) => {
            if (area < areaImagem * 0.1) return;
            const perimetro = cv.arcLength(contorno, true);
            if (perimetro < 1) return;

            for (let e = 0; e < epsilons.length; e++) {
                const aprox = new cv.Mat();
                try {
                    cv.approxPolyDP(contorno, aprox, epsilons[e] * perimetro, true);
                    if (aprox.rows === 4) {
                        const pontos = [];
                        for (let j = 0; j < 4; j++)
                            pontos.push({ x: aprox.data32S[j * 2], y: aprox.data32S[j * 2 + 1] });
                        if (area > melhorArea) {
                            melhorArea = area;
                            melhor = pontos;
                        }
                        break;
                    }
                } finally {
                    aprox.delete();
                }
            }
        });

        entradas.forEach(({ contorno }) => {
            try { contorno.delete(); } catch (e) { /* já liberado */ }
        });

        if (!melhor) return null;

        const ordenado = ordenarCantos(melhor);
        return {
            tl: { x: ordenado.tl.x * inverso, y: ordenado.tl.y * inverso },
            tr: { x: ordenado.tr.x * inverso, y: ordenado.tr.y * inverso },
            br: { x: ordenado.br.x * inverso, y: ordenado.br.y * inverso },
            bl: { x: ordenado.bl.x * inverso, y: ordenado.bl.y * inverso }
        };
    } finally {
        src.delete();
        fechado.delete();
        cinza.delete();
        borrado.delete();
        bordas.delete();
        dilatado.delete();
        contornos.delete();
        hierarquia.delete();
        kernelMorf.delete();
        kernelDilat.delete();
    }
}

function cantosPadrao(canvas) {
    const recuo = 0.08;
    const w = canvas.width;
    const h = canvas.height;
    return {
        tl: { x: w * recuo, y: h * recuo },
        tr: { x: w * (1 - recuo), y: h * recuo },
        br: { x: w * (1 - recuo), y: h * (1 - recuo) },
        bl: { x: w * recuo, y: h * (1 - recuo) }
    };
}

function calcularProporcao(c) {
    const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const larguraMedia = (d(c.tl, c.tr) + d(c.bl, c.br)) / 2;
    const alturaMedia = (d(c.tl, c.bl) + d(c.tr, c.br)) / 2;
    if (larguraMedia < 1) return 1;
    const proporcao = alturaMedia / larguraMedia;
    // Fora de uma faixa razoável, assume A4 retrato.
    if (proporcao < 0.2 || proporcao > 5) return 1754 / 1240;
    return proporcao;
}

/** Corrige a perspectiva (o extractPaper do jscanify, feito direto no cv). */
function extrairPapel(canvas, cantos, larguraSaida, alturaSaida) {
    const cv = obterCv();
    if (!cv) return null;

    const saida = document.createElement('canvas');
    const img = cv.imread(canvas);
    const distorcido = new cv.Mat();
    const origem = cv.matFromArray(4, 1, cv.CV_32FC2, [
        cantos.tl.x, cantos.tl.y,
        cantos.tr.x, cantos.tr.y,
        cantos.bl.x, cantos.bl.y,
        cantos.br.x, cantos.br.y
    ]);
    const destino = cv.matFromArray(4, 1, cv.CV_32FC2, [
        0, 0,
        larguraSaida, 0,
        0, alturaSaida,
        larguraSaida, alturaSaida
    ]);
    const matriz = cv.getPerspectiveTransform(origem, destino);

    try {
        cv.warpPerspective(img, distorcido, matriz, new cv.Size(larguraSaida, alturaSaida), cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
        cv.imshow(saida, distorcido);
        return saida;
    } finally {
        img.delete();
        distorcido.delete();
        origem.delete();
        destino.delete();
        matriz.delete();
    }
}

// ---------------------------------------------------------------------------
// Realce (normalização de iluminação)
//
// Contraste/brilho globais estouram as regiões já claras — o texto nelas vira
// branco e some. Aqui o branco do papel é estimado localmente (grade grossa +
// suavização) e cada pixel é dividido por essa estimativa: o fundo fica
// uniforme sem saturar o conteúdo, mesmo com sombra de mão/celular.
// ---------------------------------------------------------------------------

const REALCE_BRANCO_ALVO = 246;
const REALCE_GANHO_MIN = 0.9;
const REALCE_GANHO_MAX = 2.2;
const REALCE_PERCENTIL_FUNDO = 0.85;
const REALCE_PERCENTIL_PRETO = 0.015;
const REALCE_PRETO_MAX = 120;
const REALCE_GAMA = 1.12;
const REALCE_FAIXAS = 64;

function suavizarGrade(grade, colunas, linhas) {
    let atual = grade;
    for (let passo = 0; passo < 2; passo++) {
        const proxima = new Float32Array(colunas * linhas);
        for (let y = 0; y < linhas; y++) {
            for (let x = 0; x < colunas; x++) {
                let soma = 0;
                let n = 0;
                for (let dy = -1; dy <= 1; dy++) {
                    const yy = y + dy;
                    if (yy < 0 || yy >= linhas) continue;
                    for (let dx = -1; dx <= 1; dx++) {
                        const xx = x + dx;
                        if (xx < 0 || xx >= colunas) continue;
                        soma += atual[yy * colunas + xx];
                        n++;
                    }
                }
                proxima[y * colunas + x] = soma / n;
            }
        }
        atual = proxima;
    }
    return atual;
}

function gradeDeFundo(lum, w, h, celula) {
    const colunas = Math.max(1, Math.ceil(w / celula));
    const linhas = Math.max(1, Math.ceil(h / celula));
    const grade = new Float32Array(colunas * linhas);
    const faixas = new Int32Array(REALCE_FAIXAS);

    for (let cy = 0; cy < linhas; cy++) {
        const yIni = cy * celula;
        const yFim = Math.min(h, yIni + celula);
        for (let cx = 0; cx < colunas; cx++) {
            const xIni = cx * celula;
            const xFim = Math.min(w, xIni + celula);
            faixas.fill(0);
            let total = 0;
            for (let y = yIni; y < yFim; y++) {
                let idx = y * w + xIni;
                for (let x = xIni; x < xFim; x++, idx++) {
                    faixas[lum[idx] >> 2]++;
                    total++;
                }
            }
            const alvo = total * REALCE_PERCENTIL_FUNDO;
            let acumulado = 0;
            let faixa = REALCE_FAIXAS - 1;
            for (let b = 0; b < REALCE_FAIXAS; b++) {
                acumulado += faixas[b];
                if (acumulado >= alvo) { faixa = b; break; }
            }
            grade[cy * colunas + cx] = Math.max(8, faixa * 4 + 2);
        }
    }

    return { grade: suavizarGrade(grade, colunas, linhas), colunas, linhas };
}

function realcarDocumento(origem) {
    const w = origem.width;
    const h = origem.height;
    if (w < 8 || h < 8) return origem;

    const saida = document.createElement('canvas');
    saida.width = w;
    saida.height = h;
    const ctx = saida.getContext('2d', { willReadFrequently: true });
    if (!ctx) return origem;
    ctx.drawImage(origem, 0, 0);

    let imagem;
    try {
        imagem = ctx.getImageData(0, 0, w, h);
    } catch (e) {
        return saida;
    }
    const dados = imagem.data;
    const total = w * h;

    const lum = new Uint8Array(total);
    for (let p = 0, i = 0; p < total; p++, i += 4)
        lum[p] = (dados[i] * 77 + dados[i + 1] * 150 + dados[i + 2] * 29) >> 8;

    const celula = Math.max(12, Math.round(Math.min(w, h) / 24));
    const { grade, colunas, linhas } = gradeDeFundo(lum, w, h, celula);

    const xBaixo = new Int32Array(w);
    const xAlto = new Int32Array(w);
    const xFrac = new Float32Array(w);
    for (let x = 0; x < w; x++) {
        const g = Math.min(colunas - 1, Math.max(0, (x + 0.5) / celula - 0.5));
        const baixo = Math.floor(g);
        xBaixo[x] = baixo;
        xAlto[x] = Math.min(colunas - 1, baixo + 1);
        xFrac[x] = g - baixo;
    }

    const ganhos = new Float32Array(total);
    const histograma = new Int32Array(256);
    for (let y = 0; y < h; y++) {
        const g = Math.min(linhas - 1, Math.max(0, (y + 0.5) / celula - 0.5));
        const yBaixo = Math.floor(g);
        const yAlto = Math.min(linhas - 1, yBaixo + 1);
        const fy = g - yBaixo;
        const linhaBaixa = yBaixo * colunas;
        const linhaAlta = yAlto * colunas;
        const base = y * w;
        for (let x = 0; x < w; x++) {
            const fx = xFrac[x];
            const a = grade[linhaBaixa + xBaixo[x]];
            const b = grade[linhaBaixa + xAlto[x]];
            const c = grade[linhaAlta + xBaixo[x]];
            const d = grade[linhaAlta + xAlto[x]];
            const fundo = (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
            let ganho = REALCE_BRANCO_ALVO / fundo;
            if (ganho < REALCE_GANHO_MIN) ganho = REALCE_GANHO_MIN;
            else if (ganho > REALCE_GANHO_MAX) ganho = REALCE_GANHO_MAX;
            const p = base + x;
            ganhos[p] = ganho;
            const v = lum[p] * ganho;
            histograma[v > 255 ? 255 : v | 0]++;
        }
    }

    const alvoPreto = total * REALCE_PERCENTIL_PRETO;
    let acumulado = 0;
    let preto = 0;
    for (let v = 0; v < 256; v++) {
        acumulado += histograma[v];
        if (acumulado >= alvoPreto) { preto = v; break; }
    }
    if (preto > REALCE_PRETO_MAX) preto = REALCE_PRETO_MAX;

    const faixa = Math.max(32, REALCE_BRANCO_ALVO - preto);
    const lut = new Uint8Array(512);
    for (let v = 0; v < 512; v++) {
        const t = (v - preto) / faixa;
        lut[v] = t <= 0 ? 0 : t >= 1 ? 255 : Math.round(255 * Math.pow(t, REALCE_GAMA));
    }

    for (let p = 0, i = 0; p < total; p++, i += 4) {
        const ganho = ganhos[p];
        for (let k = 0; k < 3; k++) {
            const v = dados[i + k] * ganho;
            dados[i + k] = lut[v >= 511 ? 511 : v | 0];
        }
    }

    ctx.putImageData(imagem, 0, 0);
    return saida;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

const estiloModal = {
    content: {
        left: '50%',
        top: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: '760px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        overflow: 'auto',
        padding: 0
    }
};

/**
 * Props:
 *  - open: boolean
 *  - sourceFile: File da imagem a tratar
 *  - onConfirm(file): recebe o JPEG final (recortado, girado e/ou realçado)
 *  - onClose(): fecha sem alterar (também é chamado após o onConfirm)
 */
const DocumentScanner = ({ open, sourceFile, onConfirm, onClose }) => {
    const canvasOrigemRef = useRef(null);      // imagem original
    const canvasRecortadoRef = useRef(null);   // resultado do recorte (ou a original)
    const cantosAplicadosRef = useRef(null);   // cantos do recorte em vigor
    const realceCacheRef = useRef(null);       // { origem, saida } — evita reprocessar

    const canvasAjusteRef = useRef(null);
    const canvasPreviewRef = useRef(null);
    const areaAjusteRef = useRef(null);

    // 'carregando' | 'detectando' | 'ajuste' | 'processando' | 'preview' | 'erro'
    const [etapa, setEtapa] = useState('carregando');
    const [erro, setErro] = useState(null);
    const [cantos, setCantos] = useState(null);
    const [realcar, setRealcar] = useState(false);
    const [arrastando, setArrastando] = useState(null);
    const [tamanhoTela, setTamanhoTela] = useState({ w: 0, h: 0 });
    const [detectouSozinho, setDetectouSozinho] = useState(false);
    const [rotacao, setRotacao] = useState(0);

    // Abertura: carrega o OpenCV, lê a imagem e tenta o recorte automático.
    useEffect(() => {
        if (!open || !sourceFile) return undefined;
        let cancelado = false;

        setEtapa('carregando');
        setErro(null);
        setRealcar(false);
        setCantos(null);
        setDetectouSozinho(false);
        setRotacao(0);
        canvasOrigemRef.current = null;
        canvasRecortadoRef.current = null;
        cantosAplicadosRef.current = null;
        realceCacheRef.current = null;

        (async () => {
            try {
                await carregarOpenCV();
                if (cancelado) return;

                const origem = await arquivoParaCanvas(sourceFile);
                if (cancelado) return;
                canvasOrigemRef.current = origem;

                setEtapa('detectando');
                let detectados = null;
                try {
                    detectados = detectarCantos(origem);
                } catch (e) {
                    console.error('[scanner] erro na detecção:', e);
                }
                if (cancelado) return;

                if (detectados) {
                    try {
                        const altura = Math.round(LARGURA_SAIDA * calcularProporcao(detectados));
                        const recortado = extrairPapel(origem, detectados, LARGURA_SAIDA, altura);
                        if (recortado) {
                            if (cancelado) return;
                            canvasRecortadoRef.current = recortado;
                            cantosAplicadosRef.current = detectados;
                            setCantos(detectados);
                            setDetectouSozinho(true);
                            setEtapa('preview');
                            return;
                        }
                    } catch (e) {
                        // cai para o ajuste manual
                    }
                }

                // Sem detecção: mostra a original e deixa o ajuste dos cantos
                // disponível pelo botão "Editar".
                canvasRecortadoRef.current = origem;
                cantosAplicadosRef.current = null;
                setCantos(detectados || cantosPadrao(origem));
                setDetectouSozinho(false);
                setEtapa('preview');
            } catch (e) {
                if (cancelado) return;
                setErro('Falha ao processar a imagem: ' + (e && e.message ? e.message : 'erro desconhecido'));
                setEtapa('erro');
            }
        })();

        return () => { cancelado = true; };
    }, [open, sourceFile]);

    // Etapa de ajuste: desenha a original no canvas e mede a área exibida
    // (a escala dos cantos depende dela).
    useLayoutEffect(() => {
        if (etapa !== 'ajuste') return undefined;

        const origem = canvasOrigemRef.current;
        const destino = canvasAjusteRef.current;
        if (origem && destino) {
            destino.width = origem.width;
            destino.height = origem.height;
            destino.getContext('2d').drawImage(origem, 0, 0);
        }

        const area = areaAjusteRef.current;
        if (!area) return undefined;

        const medir = () => {
            const rect = area.getBoundingClientRect();
            setTamanhoTela({ w: rect.width, h: rect.height });
        };
        medir();

        if (typeof ResizeObserver === 'function') {
            const observador = new ResizeObserver(medir);
            observador.observe(area);
            return () => observador.disconnect();
        }
        window.addEventListener('resize', medir);
        return () => window.removeEventListener('resize', medir);
    }, [etapa]);

    const escala = useMemo(() => {
        const origem = canvasOrigemRef.current;
        if (!origem || tamanhoTela.w === 0) return 1;
        return tamanhoTela.w / origem.width;
    }, [tamanhoTela]);

    const ponteiroParaOrigem = useCallback((clientX, clientY) => {
        const area = areaAjusteRef.current;
        const origem = canvasOrigemRef.current;
        if (!area || !origem) return null;
        const rect = area.getBoundingClientRect();
        const x = ((clientX - rect.left) * origem.width) / rect.width;
        const y = ((clientY - rect.top) * origem.height) / rect.height;
        return {
            x: Math.max(0, Math.min(origem.width, x)),
            y: Math.max(0, Math.min(origem.height, y))
        };
    }, []);

    const iniciarArraste = (evento, chave) => {
        evento.preventDefault();
        evento.stopPropagation();
        if (evento.currentTarget.setPointerCapture)
            evento.currentTarget.setPointerCapture(evento.pointerId);
        setArrastando(chave);
    };

    const moverArraste = (evento) => {
        if (!arrastando) return;
        const p = ponteiroParaOrigem(evento.clientX, evento.clientY);
        if (!p) return;
        setCantos((atuais) => (atuais ? { ...atuais, [arrastando]: p } : atuais));
    };

    const terminarArraste = (evento) => {
        if (!arrastando) return;
        if (evento.currentTarget.releasePointerCapture) {
            try { evento.currentTarget.releasePointerCapture(evento.pointerId); } catch (e) { /* já liberado */ }
        }
        setArrastando(null);
    };

    const aplicarRecorte = useCallback(async () => {
        const origem = canvasOrigemRef.current;
        if (!origem || !cantos) return;
        setEtapa('processando');
        await new Promise((r) => requestAnimationFrame(r));

        try {
            const altura = Math.round(LARGURA_SAIDA * calcularProporcao(cantos));
            const recortado = extrairPapel(origem, cantos, LARGURA_SAIDA, altura);
            canvasRecortadoRef.current = recortado || origem;
        } catch (e) {
            canvasRecortadoRef.current = origem;
        }
        cantosAplicadosRef.current = cantos;
        setEtapa('preview');
    }, [cantos]);

    const cancelarAjuste = useCallback(() => {
        if (cantosAplicadosRef.current) setCantos(cantosAplicadosRef.current);
        setEtapa('preview');
    }, []);

    const aplicarRealce = useCallback((origem, ligado) => {
        if (!ligado) return origem;
        const cache = realceCacheRef.current;
        if (cache && cache.origem === origem) return cache.saida;
        const saida = realcarDocumento(origem);
        realceCacheRef.current = { origem, saida };
        return saida;
    }, []);

    // Preview: resultado do recorte + realce + rotação.
    useLayoutEffect(() => {
        if (etapa !== 'preview') return;
        const origem = canvasRecortadoRef.current;
        const destino = canvasPreviewRef.current;
        if (!origem || !destino) return;
        const final = girarCanvas(aplicarRealce(origem, realcar), rotacao);
        destino.width = final.width;
        destino.height = final.height;
        destino.getContext('2d').drawImage(final, 0, 0);
    }, [etapa, realcar, rotacao, aplicarRealce]);

    const confirmar = useCallback(() => {
        const origem = canvasRecortadoRef.current;
        if (!origem || !sourceFile) return;
        const final = girarCanvas(aplicarRealce(origem, realcar), rotacao);
        const nome = sourceFile.name.replace(/\.[^.]+$/, '') + '-scan.jpg';
        final.toBlob((blob) => {
            if (!blob) return;
            onConfirm(new File([blob], nome, { type: 'image/jpeg' }));
            onClose();
        }, 'image/jpeg', 0.92);
    }, [aplicarRealce, realcar, rotacao, sourceFile, onConfirm, onClose]);

    const pontosPoligono = useMemo(() => {
        if (!cantos) return '';
        return CHAVES_CANTOS.map((k) => (cantos[k].x * escala) + ',' + (cantos[k].y * escala)).join(' ');
    }, [cantos, escala]);

    const ocupado = etapa === 'carregando' || etapa === 'detectando' || etapa === 'processando';

    const switchRealce = (id) => (
        <div className="custom-control custom-switch scanner-switch">
            <input
                type="checkbox"
                className="custom-control-input"
                id={id}
                checked={realcar}
                onChange={(evento) => setRealcar(evento.target.checked)}
            />
            <label className="custom-control-label" htmlFor={id}>Realçar imagem</label>
        </div>
    );

    return (
        <ModalCadastro
            show={open}
            setShowModal={onClose}
            titulo={etapa === 'preview' ? 'Visualizar resultado' : 'Ajustar documento'}
            tamanho={estiloModal}
        >
            <div className="scanner-corpo">
                {erro &&
                    <div className="alert alert-danger py-2 mb-3">{erro}</div>}

                {ocupado && !erro &&
                    <div className="scanner-aguarde">
                        <span className="spinner-border text-primary" role="status"></span>
                        <p className="mb-0 mt-3">
                            {etapa === 'carregando' && 'Preparando processamento...'}
                            {etapa === 'detectando' && 'Detectando documento...'}
                            {etapa === 'processando' && 'Aplicando recorte...'}
                        </p>
                        {etapa === 'carregando' &&
                            <small className="text-muted">Pode levar alguns segundos no primeiro uso</small>}
                    </div>}

                {etapa === 'ajuste' && cantos &&
                    <>
                        <p className="scanner-dica">Arraste os pontos para ajustar o recorte.</p>
                        <div className="scanner-palco">
                            <div
                                ref={areaAjusteRef}
                                className="scanner-area-ajuste"
                                onPointerMove={moverArraste}
                                onPointerUp={terminarArraste}
                                onPointerCancel={terminarArraste}
                            >
                                <canvas ref={canvasAjusteRef} className="scanner-canvas" />
                                {tamanhoTela.w > 0 &&
                                    <svg className="scanner-poligono" width={tamanhoTela.w} height={tamanhoTela.h}>
                                        <polygon points={pontosPoligono} />
                                    </svg>}
                                {CHAVES_CANTOS.map((chave) =>
                                    <div
                                        key={chave}
                                        aria-label={'Canto ' + chave}
                                        className={'scanner-canto' + (arrastando === chave ? ' arrastando' : '')}
                                        onPointerDown={(evento) => iniciarArraste(evento, chave)}
                                        style={{ left: cantos[chave].x * escala - 14, top: cantos[chave].y * escala - 14 }}
                                    />)}
                            </div>
                        </div>
                        <div className="scanner-controles">{switchRealce('scanner-realce-ajuste')}</div>
                    </>}

                {etapa === 'preview' &&
                    <>
                        <p className={'scanner-dica' + (detectouSozinho ? '' : ' aviso')}>
                            {detectouSozinho
                                ? 'Recorte aplicado automaticamente. Está ok ou quer editar?'
                                : 'Não identificamos as bordas automaticamente. Confirme a imagem ou clique em "Editar" para ajustar o recorte.'}
                        </p>
                        <div className="scanner-palco">
                            <canvas ref={canvasPreviewRef} className="scanner-canvas" />
                        </div>
                        <div className="scanner-controles">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRotacao((r) => (r + 90) % 360)}>
                                <span className="icon-Repeat mr-1"></span>Girar
                            </button>
                            {switchRealce('scanner-realce-preview')}
                        </div>
                    </>}
            </div>

            <div className="scanner-rodape">
                {etapa === 'ajuste' && canvasRecortadoRef.current
                    ? <BotaoPadrao texto="Cancelar edição" type="button" classe="btn btn-outline-secondary" click={cancelarAjuste} idTooltip="cancelarEdicao" />
                    : <BotaoPadrao texto="Cancelar" type="button" classe="btn btn-outline-secondary" click={onClose} idTooltip="cancelarScanner" />}

                <div className="scanner-rodape-direita">
                    {etapa === 'ajuste' &&
                        <BotaoPadrao texto="Aplicar" type="button" click={aplicarRecorte} idTooltip="aplicarRecorte" />}
                    {etapa === 'preview' &&
                        <>
                            <BotaoPadrao texto="Editar" type="button" classe="btn btn-outline-secondary" click={() => setEtapa('ajuste')} idTooltip="editarRecorte" />
                            <BotaoPadrao texto="Confirmar" type="button" click={confirmar} idTooltip="confirmarScanner" />
                        </>}
                </div>
            </div>
        </ModalCadastro>
    );
};

export default DocumentScanner;
